import { generateUUID } from '@sveltia/utils/crypto';
import { isObject } from '@sveltia/utils/object';
import { escapeRegExp } from '@sveltia/utils/string';
import { flatten } from 'flat';

import { getIndexFile } from '$lib/services/contents/collection/entries/index-file';
import { resolveCollectionAndFile } from '$lib/services/contents/collection/files';
import { getNestedIndexFileName } from '$lib/services/contents/collection/nested';
import { hasRootField } from '$lib/services/contents/entry/fields';
import { parseEntryFile } from '$lib/services/contents/file/parse';
import { getOrCreate } from '$lib/services/utils/cache';
import { runInChunks } from '$lib/services/utils/scheduling';

/**
 * @import {
 * BaseEntryListItem,
 * Entry,
 * InternalCollection,
 * InternalEntryCollection,
 * InternalLocaleCode,
 * RepositoryFileMetadata,
 * } from '$lib/types/private';
 * @import { Field, RawEntryContent } from '$lib/types/public';
 */

/**
 * Check if the given file path is Hugo’s special index file: `_index.md` or `_index.{{locale}}.md`.
 * @param {string} path File path to be tested.
 * @returns {boolean} Result.
 */
export const isIndexFile = (path) => /\/_index(?:\.[\w-]+)?\.md$/.test(path);

/** @type {Map<string, RegExp>} */
const slugRegexCache = new Map();

/**
 * Determine the slug for the given entry content.
 * @param {object} args Arguments.
 * @param {string} args.subPath File path without the collection folder, locale and extension. It’s
 * a slug in most cases, but it may be a path containing slash(es) when the entry collection’s
 * subpath is configured.
 * @param {string | undefined} args.subPathTemplate Collection’s `subPath` configuration.
 * @returns {string} Slug.
 * @see https://decapcms.org/docs/configuration-options/#slug
 * @see https://decapcms.org/docs/collection-folder/#folder-collections-path
 * @see https://sveltiacms.app/en/docs/collections/entries
 */
export const getSlug = ({ subPath, subPathTemplate }) => {
  if (subPathTemplate?.includes('{{slug}}')) {
    const regex = getOrCreate(slugRegexCache, subPathTemplate, () => {
      // Build regex by replacing placeholders with patterns
      let regexPattern = '';
      let remaining = subPathTemplate;

      // Process template character by character, handling placeholders specially
      while (remaining.length > 0) {
        const nextPlaceholder = remaining.indexOf('{{');

        if (nextPlaceholder === -1) {
          // No more placeholders, escape remaining literal text
          regexPattern += escapeRegExp(remaining);
          break;
        }

        // Add escaped literal text before placeholder
        if (nextPlaceholder > 0) {
          regexPattern += escapeRegExp(remaining.substring(0, nextPlaceholder));
        }

        // Find end of placeholder
        const placeholderEnd = remaining.indexOf('}}', nextPlaceholder);

        if (placeholderEnd === -1) {
          // Malformed template, treat as literal
          regexPattern += escapeRegExp(remaining);
          break;
        }

        const placeholder = remaining.substring(nextPlaceholder, placeholderEnd + 2);

        regexPattern += placeholder === '{{slug}}' ? '([^/]+)' : '[^/]+?';
        remaining = remaining.substring(placeholderEnd + 2);
      }

      return new RegExp(`^${regexPattern}$`);
    });

    const [, slug] = subPath.match(regex) ?? [];

    if (slug) {
      return slug;
    }
  }

  return subPath;
};

/**
 * Parse the raw content from the file and handle any errors.
 * @param {BaseEntryListItem} file Entry file list item.
 * @param {Error[]} errors List of parse errors.
 * @returns {Promise<RawEntryContent | undefined>} Parsed content or undefined if parsing failed.
 */
export const parseFileContent = async (file, errors) => {
  try {
    return await parseEntryFile(file);
  } catch (/** @type {any} */ ex) {
    // eslint-disable-next-line no-console
    console.error(ex);
    errors.push(ex);
    return undefined;
  }
};

/**
 * Transform root field content.
 * @param {object} args Arguments.
 * @param {RawEntryContent} args.rawContent Raw content from the file.
 * @param {Field[]} args.fields Collection fields.
 * @param {boolean} args.i18nSingleFile Whether i18n single file structure is used.
 * @param {(value: any) => boolean} args.validate Function to validate content items.
 * @returns {RawEntryContent | undefined} Transformed content.
 */
export const transformRoot = ({ rawContent, fields, i18nSingleFile, validate }) => {
  const fieldName = fields[0].name;

  if (i18nSingleFile) {
    if (!isObject(rawContent) || !Object.values(rawContent).every(validate)) {
      return undefined;
    }

    return Object.fromEntries(
      Object.entries(rawContent).map(([locale, content]) => [locale, { [fieldName]: content }]),
    );
  }

  if (!validate(rawContent)) {
    return undefined;
  }

  return { [fieldName]: rawContent };
};

/**
 * Normalize `single_file_default_root` content into the standard locale-keyed format used by the
 * `single_file` structure. Root-level entries (excluding non-default locale keys) become the
 * default locale’s content, and each non-default locale key becomes that locale’s content.
 * @param {RawEntryContent} rawContent Raw content from the file.
 * @param {InternalLocaleCode[]} allLocales All available locales.
 * @param {InternalLocaleCode} defaultLocale The default locale.
 * @returns {RawEntryContent | undefined} Normalized locale-keyed content or undefined if invalid.
 */
export const normalizeDefaultRootContent = (rawContent, allLocales, defaultLocale) => {
  if (!isObject(rawContent)) {
    return undefined;
  }

  const nonDefaultLocales = allLocales.filter((locale) => locale !== defaultLocale);

  // Root-level entries excluding non-default locale keys = default locale content
  const defaultContent = Object.fromEntries(
    Object.entries(rawContent).filter(([key]) => !nonDefaultLocales.includes(key)),
  );

  /** @type {RawEntryContent} */
  const result = { [defaultLocale]: defaultContent };

  nonDefaultLocales.forEach((locale) => {
    if (isObject(rawContent[locale])) {
      result[locale] = rawContent[locale];
    }
  });

  return result;
};

/**
 * Transform raw content to handle special cases like root List and root KeyValue fields.
 * @param {RawEntryContent} rawContent Raw content from the file.
 * @param {Field[]} fields Collection fields.
 * @param {boolean} i18nSingleFile Whether i18n single file structure is used.
 * @returns {RawEntryContent | undefined} Transformed content or undefined if invalid.
 */
export const transformRawContent = (rawContent, fields, i18nSingleFile) => {
  const args = { rawContent, fields, i18nSingleFile };

  // Handle a special case: top-level List field
  if (hasRootField(fields, 'list')) {
    return transformRoot({ ...args, validate: Array.isArray });
  }

  // Handle a special case: top-level KeyValue field
  if (hasRootField(fields, 'keyvalue')) {
    return transformRoot({
      ...args,
      // Check if the key-value pairs are in an object format and each value is a string
      // eslint-disable-next-line jsdoc/require-jsdoc
      validate: (value) =>
        isObject(value) && Object.values(value).every((val) => typeof val === 'string'),
    });
  }

  return isObject(rawContent) ? rawContent : undefined;
};

/**
 * Check if the file should be skipped based on Hugo index file rules.
 * @param {string} path File path.
 * @param {string | undefined} fileName Collection file name.
 * @param {InternalCollection} collection Collection configuration.
 * @param {string | undefined} subPathTemplate Sub path template.
 * @param {string} extension File extension.
 * @returns {boolean} True if the file should be skipped.
 */
export const shouldSkipIndexFile = (path, fileName, collection, subPathTemplate, extension) => {
  // Special constraint: if template ends with _index and file follows _index pattern,
  // only .md files are allowed (Hugo constraint)
  if (
    subPathTemplate?.split('/').pop() === '_index' &&
    /\/_index(?:\.[\w-]+)?\.[\w]+$/.test(path) &&
    extension !== 'md'
  ) {
    return true;
  }

  if (!isIndexFile(path)) {
    return false;
  }

  // Skip Hugo’s special `_index.md` file that shouldn’t appear in a collection. Localized index
  // files like `_index.en.md` are also excluded by default. Exceptions:
  // 1. The collection is a file/singleton collection
  // 2. The collection is an entry collection and index file inclusion is enabled
  // 3. The collection is an entry collection and the `path` option value ends with `_index` and
  // the extension is `md`
  // 4. The collection is a nested collection where every entry is stored as `_index.md`
  return !(
    !!fileName ||
    getIndexFile(collection)?.name === '_index' ||
    (subPathTemplate?.split('/').pop() === '_index' && extension === 'md') ||
    getNestedIndexFileName(collection) === '_index'
  );
};

/**
 * Extract subPath and locale information from the file path.
 * @param {BaseEntryListItem} file Entry file list item.
 * @param {string | undefined} fileName Collection file name.
 * @param {RegExp | undefined} fullPathRegEx Full path regex for parsing.
 * @param {InternalLocaleCode} defaultLocale Default locale.
 * @param {boolean} isMultiFileStructure Whether using multi-file i18n structure.
 * @returns {{ subPath: string | undefined, locale: InternalLocaleCode | undefined }} Path info.
 */
export const extractPathInfo = (
  file,
  fileName,
  fullPathRegEx,
  defaultLocale,
  isMultiFileStructure,
) => {
  const {
    path,
    folder: { filePathMap },
  } = file;

  if (fileName) {
    if (isMultiFileStructure) {
      const [locale, subPath] =
        Object.entries(filePathMap ?? {}).find(([, locPath]) => locPath === path) ?? [];

      return { subPath, locale };
    }

    return { subPath: path, locale: undefined };
  }

  if (!fullPathRegEx) {
    return { subPath: undefined, locale: undefined };
  }

  // If the `omit_default_locale_from_file_path` i18n option is enabled, the matching comes with
  // the `locale` group being `undefined` for the default locale, so we need a fallback for it
  const match = path.match(fullPathRegEx);

  if (!match?.groups) {
    return { subPath: undefined, locale: undefined };
  }

  const { subPath, locale = defaultLocale } = match.groups;

  return { subPath, locale };
};

/**
 * Process entry for non-i18n collections.
 * @param {Entry} entry Entry object to populate.
 * @param {RawEntryContent} rawContent Raw content.
 * @param {string} path File path.
 * @param {string | undefined} fileName Collection file name.
 * @param {string} subPath Sub path.
 * @param {string | undefined} subPathTemplate Sub path template.
 */
export const processNonI18nEntry = (
  entry,
  rawContent,
  path,
  fileName,
  subPath,
  subPathTemplate,
) => {
  const slug = fileName || getSlug({ subPath, subPathTemplate });

  entry.slug = slug;
  entry.locales._default = { slug, path, content: flatten(rawContent) };
};

/**
 * Process entry for single-file i18n structure.
 * @param {Entry} entry Entry object to populate.
 * @param {RawEntryContent} rawContent Raw content.
 * @param {string} path File path.
 * @param {string | undefined} fileName Collection file name.
 * @param {string} subPath Sub path.
 * @param {string | undefined} subPathTemplate Sub path template.
 * @param {InternalLocaleCode[]} allLocales All available locales.
 */
export const processI18nSingleFileEntry = (
  entry,
  rawContent,
  path,
  fileName,
  subPath,
  subPathTemplate,
  allLocales,
) => {
  const slug = fileName || getSlug({ subPath, subPathTemplate });

  entry.slug = slug;
  entry.locales = Object.fromEntries(
    allLocales
      .filter((_locale) => _locale in rawContent)
      .map((_locale) => [_locale, { slug, path, content: flatten(rawContent[_locale]) }]),
  );
};

/**
 * Process entry for multi-file i18n structure.
 * @param {Entry} entry Entry object to populate.
 * @param {RawEntryContent} rawContent Raw content.
 * @param {string} path File path.
 * @param {string | undefined} fileName Collection file name.
 * @param {string} subPath Sub path.
 * @param {string | undefined} subPathTemplate Sub path template.
 * @param {InternalLocaleCode} locale Current locale.
 * @param {InternalLocaleCode} defaultLocale Default locale.
 * @param {string} collectionName Collection name.
 * @param {string | undefined} canonicalSlugKey Canonical slug key.
 * @param {Map<string, Entry>} entryMap Entries prepared so far, keyed by their temporary ID. A new
 * entry is registered here so that its other locales can find it.
 * @returns {boolean} True if entry was added to existing entry, false if new entry should be added.
 */
export const processI18nMultiFileEntry = (
  entry,
  rawContent,
  path,
  fileName,
  subPath,
  subPathTemplate,
  locale,
  defaultLocale,
  collectionName,
  canonicalSlugKey,
  entryMap,
) => {
  // Support a canonical slug to link localized files
  const canonicalSlug =
    canonicalSlugKey && typeof rawContent[canonicalSlugKey] === 'string'
      ? rawContent[canonicalSlugKey]
      : undefined;

  const slug = fileName || getSlug({ subPath, subPathTemplate });
  const localizedEntry = { slug, path, content: flatten(rawContent) };
  // Use a temporary ID to locate all the localized files for the entry
  const tempId = `${collectionName}/${canonicalSlug ?? slug}`;
  // Check if the entry has already been added for another locale. A lookup in the map rather than
  // a scan of the entry list keeps this linear over a repository with thousands of localized files
  const existingEntry = entryMap.get(tempId);

  // If found, add a new locale to the existing entry; don’t add another entry
  if (existingEntry) {
    existingEntry.locales[locale] = localizedEntry;

    // The default locale defines the entry’s identity, but any other locale stands in when its file
    // is absent. Otherwise the entry would have no slug and `prepareEntries()` would drop it, which
    // happens with Editorial Workflow whenever a pull request touches only a non-default locale
    if (locale === defaultLocale || !existingEntry.slug) {
      existingEntry.slug = slug;
      existingEntry.subPath = subPath;
    }

    return true; // Entry was merged with existing
  }

  entry.id = tempId;
  entry.locales[locale] = localizedEntry;
  // The default locale file overrides this when it’s processed, in the branch above
  entry.slug = slug;
  entryMap.set(tempId, entry);

  return false; // New entry should be added
};

/**
 * Raw items of the files storing all the entries of an entry collection, keyed by file path, as
 * they were last parsed. A save rewrites the file from these rather than from the entries, so the
 * items that don’t make an entry, and the properties that aren’t defined as fields, are kept as
 * they are. `null` for a file that doesn’t contain a valid array, which can’t be updated.
 * @type {Map<string, any[] | null>}
 */
export const arrayFileItems = new Map();

/**
 * Create an entry from an item in a file storing all the entries of an entry collection. With i18n
 * enabled, the item holds all the translations, like a file with the `single_file` or
 * `single_file_default_root` structure does.
 * @param {object} args Arguments.
 * @param {InternalEntryCollection} args.collection Collection.
 * @param {any} args.item Raw item.
 * @param {number} args.index Position of the item in the array.
 * @param {string} args.path File path.
 * @param {RepositoryFileMetadata} [args.meta] File metadata.
 * @returns {Entry | undefined} Entry, or `undefined` if the item doesn’t make one.
 */
export const createArrayItemEntry = ({ collection, item, index, path, meta = {} }) => {
  const {
    fields,
    _i18n: {
      i18nEnabled,
      allLocales,
      defaultLocale,
      structureMap: { i18nSingleFileDefaultRoot },
    },
  } = collection;

  const rawContent = i18nSingleFileDefaultRoot
    ? normalizeDefaultRootContent(item, allLocales, defaultLocale)
    : item;

  const content = rawContent ? transformRawContent(rawContent, fields, i18nEnabled) : undefined;

  if (!content) {
    return undefined;
  }

  // The position is the slug, as an item has no file name to take one from
  const subPath = String(index);
  /** @type {Entry} */
  const entry = { id: '', slug: '', subPath, locales: {}, arrayIndex: index, ...meta };

  if (i18nEnabled) {
    processI18nSingleFileEntry(entry, content, path, undefined, subPath, undefined, allLocales);
  } else {
    processNonI18nEntry(entry, content, path, undefined, subPath, undefined);
  }

  return Object.keys(entry.locales).length ? entry : undefined;
};

/**
 * Prepare the entries stored in a file of an entry collection that stores all the entries in one
 * file, one for each object in the array.
 * @param {object} args Arguments.
 * @param {InternalEntryCollection} args.collection Collection.
 * @param {BaseEntryListItem} args.file Entry file list item.
 * @param {any} args.rawContent Parsed file content.
 * @param {Entry[]} args.entries List of prepared entries.
 * @param {Error[]} args.errors List of parse errors.
 */
export const prepareArrayFileEntries = ({ collection, file, rawContent, entries, errors }) => {
  const { path, meta = {} } = file;

  if (!Array.isArray(rawContent)) {
    arrayFileItems.set(path, null);
    errors.push(new Error(`${path} could not be parsed, as it doesn’t contain an array.`));

    return;
  }

  arrayFileItems.set(path, rawContent);

  rawContent.forEach((item, index) => {
    const entry = createArrayItemEntry({ collection, item, index, path, meta });

    if (entry) {
      entries.push(entry);
    }
  });
};

/**
 * Prepare a new entry by processing the given file info and raw content.
 * @param {object} args Arguments.
 * @param {BaseEntryListItem} args.file Entry file list item.
 * @param {Entry[]} args.entries List of prepared entries.
 * @param {Map<string, Entry>} args.entryMap Prepared entries keyed by their temporary ID, used to
 * merge the localized files of a multi-file i18n entry.
 * @param {Error[]} args.errors List of parse errors.
 */
export const prepareEntry = async ({ file, entries, entryMap, errors }) => {
  const rawContent = await parseFileContent(file, errors);

  if (!rawContent) {
    const { collection, collectionFile } =
      resolveCollectionAndFile(file.folder.collectionName, file.folder.fileName) ?? {};

    // Make sure a file storing all the entries of an entry collection is not overwritten
    if (!collectionFile && collection?._type === 'entry' && collection._file.arrayFile) {
      arrayFileItems.set(file.path, null);
    }

    return;
  }

  const {
    path,
    meta = {},
    folder: { collectionName, fileName },
  } = file;

  const resolved = resolveCollectionAndFile(collectionName, fileName);

  if (!resolved) {
    return;
  }

  const { collection, collectionFile } = resolved;

  if (!collectionFile && collection._type === 'entry' && collection._file.arrayFile) {
    prepareArrayFileEntries({ collection, file, rawContent, entries, errors });

    return;
  }

  const {
    fields = [],
    _file: { fullPathRegEx, subPath: subPathTemplate, extension },
    _i18n: {
      i18nEnabled,
      allLocales,
      defaultLocale,
      structureMap: {
        i18nSingleFile,
        i18nSingleFileDefaultRoot,
        i18nMultiFile,
        i18nMultiFolder,
        i18nMultiRootFolder,
      },
      canonicalSlug: { key: canonicalSlugKey },
    },
  } = collectionFile ?? /** @type {InternalEntryCollection} */ (collection);

  // Normalize `single_file_default_root` content into the standard locale-keyed format so that the
  // rest of the processing pipeline can treat it identically to `single_file`.
  const effectiveRawContent = i18nSingleFileDefaultRoot
    ? normalizeDefaultRootContent(rawContent, allLocales, defaultLocale)
    : rawContent;

  if (!effectiveRawContent) {
    return;
  }

  const isI18nSingleFile = i18nSingleFile || i18nSingleFileDefaultRoot;
  const transformedContent = transformRawContent(effectiveRawContent, fields, isI18nSingleFile);

  if (!transformedContent) {
    return;
  }

  if (shouldSkipIndexFile(path, fileName, collection, subPathTemplate, extension)) {
    return;
  }

  const isMultiFileStructure = i18nMultiFile || i18nMultiFolder || i18nMultiRootFolder;

  const { subPath, locale } = extractPathInfo(
    file,
    fileName,
    fullPathRegEx,
    defaultLocale,
    isMultiFileStructure,
  );

  if (!subPath) {
    return;
  }

  if (isMultiFileStructure && !locale) {
    return;
  }

  /** @type {Entry} */
  const entry = {
    id: '',
    slug: '',
    subPath,
    locales: {},
    ...meta,
  };

  if (!i18nEnabled) {
    processNonI18nEntry(entry, transformedContent, path, fileName, subPath, subPathTemplate);
  } else if (i18nSingleFile || i18nSingleFileDefaultRoot) {
    processI18nSingleFileEntry(
      entry,
      transformedContent,
      path,
      fileName,
      subPath,
      subPathTemplate,
      allLocales,
    );
  } else {
    // `isMultiFileStructure` is always true here (the only non-`i18nSingleFile` path), and `locale`
    // is always set (the guard above returned early if it wasn’t).
    const wasMerged = processI18nMultiFileEntry(
      entry,
      transformedContent,
      path,
      fileName,
      subPath,
      subPathTemplate,
      /** @type {InternalLocaleCode} */ (locale),
      defaultLocale,
      collectionName,
      canonicalSlugKey,
      entryMap,
    );

    if (wasMerged) {
      return; // Entry was merged with existing, don’t add to entries array
    }
  }

  entries.push(entry);
};

/**
 * Parse the given entry files to create a complete, serialized entry list.
 * @param {BaseEntryListItem[]} entryFiles Entry file list.
 * @returns {Promise<{ entries: Entry[], errors: Error[] }>} Entry list and error list.
 */
export const prepareEntries = async (entryFiles) => {
  /** @type {Entry[]} */
  const entries = [];
  /** @type {Map<string, Entry>} */
  const entryMap = new Map();
  /** @type {Error[]} */
  const errors = [];

  // Parsing is synchronous, so a large repository would freeze the tab for the whole batch while
  // the progress indicator stalls. Yield between chunks instead
  await runInChunks(entryFiles, (file) => prepareEntry({ file, entries, entryMap, errors }));

  return {
    entries: entries.filter((entry) => {
      // Override a temporary ID
      entry.id = generateUUID();

      return !!entry.slug && !!Object.keys(entry.locales).length;
    }),
    errors,
  };
};
